import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Bell, Plus, Trash2, Send, Users, CheckCircle2, Clock, Eye,
  AlertTriangle, Info, X, UserCheck, UserX, Monitor
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminNotifications = ({ user, onLogout }) => {
  const [notifications, setNotifications] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState({ open: false, notification: null, details: null });
  
  // Form state
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('normal');
  const [recipientType, setRecipientType] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchNotifications();
    fetchUsers();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      toast.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handleCreateNotification = async (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const recipients = recipientType === 'all' ? ['all'] : recipientType === 'tv_only' ? ['tv_only'] : selectedUsers;
      
      if (recipientType === 'select' && selectedUsers.length === 0) {
        toast.error('Please select at least one recipient');
        setSending(false);
        return;
      }
      
      await axios.post(`${BACKEND_URL}/api/admin/notifications`, {
        title,
        message,
        priority,
        recipients
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Notification sent successfully!');
      setCreateDialog(false);
      resetForm();
      fetchNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notification deleted');
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const viewNotificationDetails = async (notification) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/notifications/${notification.id}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetailDialog({
        open: true,
        notification,
        details: response.data
      });
    } catch (error) {
      toast.error('Failed to load details');
    }
  };

  const resetForm = () => {
    setTitle('');
    setMessage('');
    setPriority('normal');
    setRecipientType('all');
    setSelectedUsers([]);
  };

  const toggleUserSelection = (email) => {
    setSelectedUsers(prev => 
      prev.includes(email) 
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-700';
      case 'important':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-blue-100 text-blue-700';
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 p-8 overflow-auto" data-testid="admin-notifications">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 bg-clip-text text-transparent mb-2">
                Broadcast Notifications
              </h1>
              <p className="text-slate-600 flex items-center gap-2">
                <Bell className="w-4 h-4 text-blue-500" />
                <span>Send announcements and track acknowledgements</span>
              </p>
            </div>
            <Button
              onClick={() => setCreateDialog(true)}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Notification
            </Button>
          </div>

          {/* Notifications List */}
          <Card className="border-0 shadow-xl">
            <CardHeader className="border-b bg-gradient-to-r from-slate-50 to-white">
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-blue-600" />
                All Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-gradient-to-r from-slate-100 to-slate-50">
                    <TableRow>
                      <TableHead className="font-semibold">Title</TableHead>
                      <TableHead className="font-semibold">Priority</TableHead>
                      <TableHead className="font-semibold">Sent</TableHead>
                      <TableHead className="font-semibold text-center">Acknowledged</TableHead>
                      <TableHead className="font-semibold text-center">Pending</TableHead>
                      <TableHead className="font-semibold text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : notifications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No notifications sent yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      notifications.map((notif, index) => (
                        <TableRow key={index} className="hover:bg-slate-50/80 transition-colors">
                          <TableCell>
                            <div>
                              <span className="font-medium text-slate-800">{notif.title}</span>
                              <p className="text-xs text-slate-500 truncate max-w-xs">{notif.message}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityBadge(notif.priority)}`}>
                              {notif.priority}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {new Date(notif.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                              <span className="font-semibold text-emerald-600">{notif.acknowledged_count}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Clock className="w-4 h-4 text-amber-500" />
                              <span className="font-semibold text-amber-600">{notif.pending_count}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => viewNotificationDetails(notif)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteNotification(notif.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </main>

      {/* Create Notification Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" />
              Send Notification
            </DialogTitle>
            <DialogDescription>
              Create a pop-up notification for users. They will see it on their next login.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateNotification} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                placeholder="e.g., Important Announcement"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Message</Label>
              <textarea
                className="w-full px-3 py-2 border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                placeholder="Enter your message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">
                    <div className="flex items-center gap-2">
                      <Info className="w-4 h-4 text-blue-500" />
                      Normal
                    </div>
                  </SelectItem>
                  <SelectItem value="important">
                    <div className="flex items-center gap-2">
                      <Bell className="w-4 h-4 text-amber-500" />
                      Important
                    </div>
                  </SelectItem>
                  <SelectItem value="urgent">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      Urgent
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Recipients</Label>
              <Select value={recipientType} onValueChange={setRecipientType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      All Users
                    </div>
                  </SelectItem>
                  <SelectItem value="select">
                    <div className="flex items-center gap-2">
                      <UserCheck className="w-4 h-4" />
                      Select Users
                    </div>
                  </SelectItem>
                  <SelectItem value="tv_only">
                    <div className="flex items-center gap-2">
                      <Monitor className="w-4 h-4" />
                      Only TV Display
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {recipientType === 'select' && (
              <div className="space-y-2 border rounded-lg p-3 max-h-48 overflow-y-auto">
                <Label className="text-sm text-slate-500">Select recipients ({selectedUsers.length} selected)</Label>
                {allUsers.map((u) => (
                  <div key={u.email} className="flex items-center gap-2 py-1">
                    <Checkbox
                      checked={selectedUsers.includes(u.email)}
                      onCheckedChange={() => toggleUserSelection(u.email)}
                    />
                    <span className="text-sm">{u.agent_name}</span>
                    <span className="text-xs text-slate-400">({u.role})</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => { setCreateDialog(false); resetForm(); }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600"
                disabled={sending}
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Sending...' : 'Send Notification'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Notification Details Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => setDetailDialog({ ...detailDialog, open })}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Notification Details
            </DialogTitle>
          </DialogHeader>

          {detailDialog.notification && detailDialog.details && (
            <div className="space-y-4 mt-4">
              {/* Full Message Preview */}
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-bold text-lg text-slate-800">{detailDialog.notification.title}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityBadge(detailDialog.notification.priority)}`}>
                    {detailDialog.notification.priority}
                  </span>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200 whitespace-pre-wrap text-slate-700 text-sm max-h-48 overflow-y-auto">
                  {detailDialog.notification.message}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Sent on {new Date(detailDialog.notification.created_at).toLocaleString()}
                </p>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <UserCheck className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-emerald-700">
                    {detailDialog.details.acknowledged_users.length}
                  </p>
                  <p className="text-xs text-emerald-600">Acknowledged</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <UserX className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-amber-700">
                    {detailDialog.details.pending_users.length}
                  </p>
                  <p className="text-xs text-amber-600">Pending</p>
                </div>
              </div>

              {/* Acknowledged Users */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  Acknowledged ({detailDialog.details.acknowledged_users.length})
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {detailDialog.details.acknowledged_users.length === 0 ? (
                    <p className="text-sm text-slate-400">No acknowledgements yet</p>
                  ) : (
                    detailDialog.details.acknowledged_users.map((u, i) => (
                      <div key={i} className="flex items-center justify-between bg-emerald-50 rounded-lg px-3 py-2">
                        <span className="text-sm font-medium text-slate-700">{u.name}</span>
                        <span className="text-xs text-slate-500">
                          {new Date(u.acknowledged_at).toLocaleString()}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Pending Users */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-500" />
                  Pending ({detailDialog.details.pending_users.length})
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {detailDialog.details.pending_users.length === 0 ? (
                    <p className="text-sm text-emerald-600">All users have acknowledged!</p>
                  ) : (
                    detailDialog.details.pending_users.map((u, i) => (
                      <div key={i} className="bg-amber-50 rounded-lg px-3 py-2">
                        <span className="text-sm font-medium text-slate-700">{u.name}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <Button 
                className="w-full"
                onClick={() => setDetailDialog({ open: false, notification: null, details: null })}
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminNotifications;
